import React, { useRef, useState } from 'react';
import { useAsync } from 'react-async';
import { Header, Button } from '../../components';
import { MdList } from 'react-icons/md';
import { Stack, SegmentedControl } from '@tymate/margaret';
import LocationAwareSearch from '../../components/LocationAwareSearch';
import { Select } from 'components/Fields';
import { useApp, useError, useSearchParams } from 'hooks';
import { IoMapOutline } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import ExperiencesListComponent from './List/partials/ExperiencesList';
import { post } from 'api/node';
import { useDeepCompareEffect } from 'react-use';
import { isEqual } from 'lodash';
import { Link } from '../../ui';
import ExperiencesMap from './List/partials/ExperiencesMap';
import { exportExperiencesCompatibilities } from 'api/node/experiences';
import { GET_ORGANIZATIONS } from 'components/Forms/UserForm';
import { useQuery } from '@apollo/client';

const getList = async ({ filters }) => {
  return await post('/experiences/liste', filters);
};

export default function ExperiencesList() {
  const { t } = useTranslation('experiences');
  const headerRef = useRef();
  const [searchParams, setSearchParams] = useSearchParams();
  const [kind, setKind] = useState('list');
  const [type, setType] = useState(undefined);
  const [selectedOrganization, setSelectedOrganization] = useState(undefined);
  const { state } = searchParams;
  const [page, setPage] = useState(1);
  const limitPerPage = 9;
  const [hasNextPage, setHasNextPage] = useState(true);
  const [experiences, setExperiences] = useState([]);
  const [extractProcessing, setExtractProcessing] = useState(false);
  const { user } = useApp();
  const experiencesFilters = {
    page,
    state: state ?? '',
    type: type?.value ?? '',
    organizationId: selectedOrganization?.value ?? '',
    search: searchParams.search ?? '',
    perPage: limitPerPage,
  };

  const { sendErrorSnack } = useError();
  const { data, isLoading } = useAsync({
    promiseFn: getList,
    filters: experiencesFilters,
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
  });
  const stateOptions = [
    { value: null, label: t('state.all') },
    { value: 'available', label: t('state.available') },
    { value: 'in_progress', label: t('state.inProgress') },
    { value: 'stand_by', label: t('state.standBy') },
  ];
  const isEmpty = experiences.length === 0;

  const handleLoadNextPage = () => {
    setPage(page => page + 1);
  };

  useDeepCompareEffect(() => {
    if (data?.data) {
      setExperiences(experiences => [...experiences, ...data.data]);
      if (data?.data.length < limitPerPage) {
        setHasNextPage(false);
      }
    }
  }, [{ data }]);

  useDeepCompareEffect(() => {
    setPage(1);
    setHasNextPage(true);
    setExperiences([]);
  }, [{ state, type, searchParams, selectedOrganization }]);

  const { data: organizationData } = useQuery(GET_ORGANIZATIONS, {
    variables: {
      discarded: false,
      isPartner: false,
    },
  });
  const organizationOptions = (
    organizationData?.organizations?.nodes ?? []
  )?.map(({ id, displayName }) => ({
    label: displayName,
    value: id,
  }));
  organizationOptions.unshift({
    label: 'Select organization',
    value: undefined,
  });

  const extractExperiencesCompatibilities = async () => {
    setExtractProcessing(true);
    try {
      const response = await exportExperiencesCompatibilities({
        filters: experiencesFilters,
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const href = window.URL.createObjectURL(blob);

      const anchorElement = document.createElement('a');
      anchorElement.href = href;
      anchorElement.download = 'experiences-compatibitilies.xlsx';
      document.body.appendChild(anchorElement);
      anchorElement.click();
      document.body.removeChild(anchorElement);
      window.URL.revokeObjectURL(href);
    } catch (error) {
      sendErrorSnack(error);
    } finally {
      setExtractProcessing(false);
    }
  };

  const headerHeight = headerRef.current?.offsetHeight || 0;

  return (
    <>
      <Header
        ref={headerRef}
        variant={kind === 'map' ? 'fixed' : undefined}
        rightElement={
          <Link to={'create'}>
            <Button variant="primary">{t('create')}</Button>
          </Link>
        }
      >
        <Stack
          alignX="space-between"
          alignY="center"
          size="full"
          gutterSize={1}
        >
          <Stack gutterSize={1}>
            <LocationAwareSearch name="search" />
            <Select
              value={type}
              onChange={value => setType(value)}
              name="Topology"
              options={[
                { label: t('state.all'), value: undefined },
                { label: t('transfer'), value: 'Transfer' },
                { label: t('types.activity'), value: 'Activity' },
                { label: t('types.extra'), value: 'Extra' },
                { label: t('types.program'), value: 'Program' },
              ]}
            />
            {user.role === 'koob_admin' && (
              <Select
                value={selectedOrganization}
                onChange={value => setSelectedOrganization(value)}
                name="organization"
                options={organizationOptions}
              />
            )}
            <Button
              type="button"
              variant="primary"
              onClick={extractExperiencesCompatibilities}
              disabled={extractProcessing || isEmpty}
            >
              {t('extractCompatibilities')}
            </Button>
          </Stack>

          <Stack alignY="center" gutterSize={1}>
            <SegmentedControl
              options={stateOptions}
              value={state ?? null}
              onSelect={value =>
                setSearchParams({
                  ...searchParams,
                  state: value,
                })
              }
            />
            <SegmentedControl
              value={kind}
              onSelect={value => setKind(value)}
              options={[
                { value: 'list', label: <MdList size={20} /> },
                { value: 'map', label: <IoMapOutline size={20} /> },
              ]}
            />
          </Stack>
        </Stack>
      </Header>
      {kind === 'list' && (
        <ExperiencesListComponent
          experiences={experiences}
          onLoadNextPage={handleLoadNextPage}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isEmpty={isEmpty}
        />
      )}
      {kind === 'map' && (
        <ExperiencesMap
          experiences={experiences}
          onLoadNextPage={handleLoadNextPage}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isEmpty={isEmpty}
          headerHeight={headerHeight}
        />
      )}
    </>
  );
}
